import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

type Trigger =
  | {
      kind: 'inactivity';
      after_minutes: number;
      warn_before_minutes: number;
      from_role: 'user' | 'any' | 'support';
    }
  | { kind: 'max_age'; after_hours: number }
  | { kind: 'absolute_time'; iso: string }
  | { kind: 'schedule_cron'; cron: string; tz?: string };

export interface Policy {
  triggers: Trigger[];
  permissions: { user_can_close: boolean; support_can_close: boolean; admin_can_close: boolean };
  actions: {
    on_warning: 'post_embed_with_extend_button' | 'none';
    extend_minutes: number;
    on_close: 'transcript_then_archive' | 'transcript_then_delete' | 'archive_only';
  };
  auto_reopen_on_message: boolean;
}

interface Props {
  value: Policy;
  onChange: (next: Policy) => void;
}

const KINDS: Array<{ kind: Trigger['kind']; label: string }> = [
  { kind: 'inactivity', label: 'Inactivity timeout' },
  { kind: 'max_age', label: 'Max age' },
  { kind: 'absolute_time', label: 'Absolute time' },
  { kind: 'schedule_cron', label: 'Cron schedule' },
];

export function ClosePolicyEditor({ value, onChange }: Props) {
  function updateTrigger(i: number, patch: Partial<Trigger>) {
    const next = value.triggers.slice();
    next[i] = { ...(next[i] as object), ...(patch as object) } as Trigger;
    onChange({ ...value, triggers: next });
  }
  function addTrigger() {
    onChange({
      ...value,
      triggers: [
        ...value.triggers,
        { kind: 'inactivity', after_minutes: 1440, warn_before_minutes: 60, from_role: 'user' },
      ],
    });
  }
  function removeTrigger(i: number) {
    onChange({ ...value, triggers: value.triggers.filter((_, idx) => idx !== i) });
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Triggers ({value.triggers.length})</h3>
        {value.triggers.map((t, i) => (
          <div key={i} className="rounded-md border border-input p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Select
                value={t.kind}
                onValueChange={(kind) => {
                  const next = (
                    kind === 'inactivity'
                      ? {
                          kind,
                          after_minutes: 1440,
                          warn_before_minutes: 60,
                          from_role: 'user' as const,
                        }
                      : kind === 'max_age'
                        ? { kind, after_hours: 720 }
                        : kind === 'absolute_time'
                          ? { kind, iso: new Date(Date.now() + 86400_000).toISOString() }
                          : { kind, cron: '0 3 * * 0', tz: 'UTC' }
                  ) as Trigger;
                  updateTrigger(i, next as Partial<Trigger>);
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.kind} value={k.kind}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => removeTrigger(i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <TriggerFields value={t} onChange={(patch) => updateTrigger(i, patch)} />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addTrigger}>
          <Plus className="mr-2 h-4 w-4" />
          Add trigger
        </Button>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Permissions</h3>
        <Toggle
          label="User can close their own ticket"
          checked={value.permissions.user_can_close}
          onChange={(b) =>
            onChange({ ...value, permissions: { ...value.permissions, user_can_close: b } })
          }
        />
        <Toggle
          label="Support can close"
          checked={value.permissions.support_can_close}
          onChange={(b) =>
            onChange({ ...value, permissions: { ...value.permissions, support_can_close: b } })
          }
        />
        <Toggle
          label="Admin can close"
          checked={value.permissions.admin_can_close}
          onChange={(b) =>
            onChange({ ...value, permissions: { ...value.permissions, admin_can_close: b } })
          }
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Actions</h3>
        <div className="space-y-2">
          <Label>On close</Label>
          <Select
            value={value.actions.on_close}
            onValueChange={(v) =>
              onChange({
                ...value,
                actions: { ...value.actions, on_close: v as Policy['actions']['on_close'] },
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="transcript_then_delete">
                Save transcript, delete channel
              </SelectItem>
              <SelectItem value="transcript_then_archive">
                Save transcript, move to archive category
              </SelectItem>
              <SelectItem value="archive_only">Move to archive (no transcript)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Switch checked={checked} onCheckedChange={onChange} />
      {label}
    </label>
  );
}

function TriggerFields({
  value,
  onChange,
}: {
  value: Trigger;
  onChange: (patch: Partial<Trigger>) => void;
}) {
  if (value.kind === 'inactivity') {
    return (
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label>after_minutes</Label>
          <Input
            type="number"
            value={value.after_minutes}
            onChange={(e) =>
              onChange({ after_minutes: Number(e.target.value) } as Partial<Trigger>)
            }
          />
        </div>
        <div className="space-y-1">
          <Label>warn_before_minutes</Label>
          <Input
            type="number"
            value={value.warn_before_minutes}
            onChange={(e) =>
              onChange({ warn_before_minutes: Number(e.target.value) } as Partial<Trigger>)
            }
          />
        </div>
        <div className="space-y-1">
          <Label>from_role</Label>
          <Select
            value={value.from_role}
            onValueChange={(v) =>
              onChange({ from_role: v as 'user' | 'any' | 'support' } as Partial<Trigger>)
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="user">user (opener)</SelectItem>
              <SelectItem value="any">any</SelectItem>
              <SelectItem value="support">support</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }
  if (value.kind === 'max_age') {
    return (
      <div className="space-y-1">
        <Label>after_hours</Label>
        <Input
          type="number"
          value={value.after_hours}
          onChange={(e) => onChange({ after_hours: Number(e.target.value) } as Partial<Trigger>)}
        />
      </div>
    );
  }
  if (value.kind === 'absolute_time') {
    return (
      <div className="space-y-1">
        <Label>ISO datetime</Label>
        <Input
          value={value.iso}
          onChange={(e) => onChange({ iso: e.target.value } as Partial<Trigger>)}
        />
      </div>
    );
  }
  if (value.kind === 'schedule_cron') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label>cron</Label>
          <Input
            value={value.cron}
            onChange={(e) => onChange({ cron: e.target.value } as Partial<Trigger>)}
          />
        </div>
        <div className="space-y-1">
          <Label>tz (IANA)</Label>
          <Input
            value={value.tz ?? ''}
            onChange={(e) => onChange({ tz: e.target.value || undefined } as Partial<Trigger>)}
            placeholder="UTC"
          />
        </div>
      </div>
    );
  }
  return null;
}
